import { call, put, takeLatest } from 'redux-saga/effects';
import { YieldOf } from 'types/utils';
import { Constants } from 'core';

import { createReason, createdReason, updateReason, updatedReason, deleteReason, deletedReason } from './actions';
import { fetchReasons } from '../reasons/actions';
import { logout } from '../api-auth/actions';
import getAPI from '../../api/iv-back';
import { sagaWithCallback } from '../utils';

type CreateReasonActionPayload = ReturnType<typeof createReason>;
type UpdateReasonActionPayload = ReturnType<typeof updateReason>;
type DeleteReasonActionPayload = ReturnType<typeof deleteReason>;

export function* createReasonSaga({ payload }: CreateReasonActionPayload) {
  const { createReasonApi } = getAPI();
  const response: YieldOf<typeof createReasonApi> = yield call(createReasonApi, payload);
  const { data, ok, problem, status } = response;
  if (status === 401 || status === 403) {
    yield put(logout());
  }
  if (ok) {
    yield put(fetchReasons());
    yield put(createdReason(data));
    return data;
  } else {
    const error =
      status === Constants.ALREADY_EXISTED_STATUS ? Constants.ALREADY_EXISTED_PROBLEM_CODE : problem || 'unknown';
    yield put(createdReason(undefined, error));
    throw new Error('create reason failed: ' + error);
  }
}

export function* updateReasonSaga({ payload }: UpdateReasonActionPayload) {
  const { updateReasonApi } = getAPI();
  const response: YieldOf<typeof updateReasonApi> = yield call(updateReasonApi, payload);
  const { data, ok, problem, status } = response;
  if (status === 401 || status === 403) {
    yield put(logout());
  }
  if (ok) {
    yield put(fetchReasons());
    yield put(updatedReason(data));
    return data;
  } else {
    const error =
      status === Constants.ALREADY_EXISTED_STATUS ? Constants.ALREADY_EXISTED_PROBLEM_CODE : problem || 'unknown';
    yield put(updatedReason(undefined, error));
    throw new Error('update reason failed: ' + error);
  }
}

export function* deleteReasonSaga({ meta: { id } }: DeleteReasonActionPayload) {
  const { deleteReasonApi } = getAPI();
  const response: YieldOf<typeof deleteReasonApi> = yield call(deleteReasonApi, id);
  const { ok, problem, status } = response;
  if (status === 401 || status === 403) {
    yield put(logout());
  }
  if (ok) {
    yield put(fetchReasons());
    yield put(deletedReason(true));
  } else {
    const error = problem || 'unknown';
    yield put(deletedReason(false, error));
  }
}

export default function* ReasonSaga() {
  yield takeLatest(createReason.type, sagaWithCallback(createReasonSaga));
  yield takeLatest(updateReason.type, sagaWithCallback(updateReasonSaga));
  yield takeLatest(deleteReason.type, deleteReasonSaga);
}
